import React, { useState, useEffect, useRef } from 'react';
import styles from './styles/CreateSidebar.module.css';
import { FaShapes, FaIndent, FaSortNumericDown, FaKey, FaCog, FaList, FaSignOutAlt,
    FaCopy, FaEye, FaSearch, FaExclamationCircle, FaCheckCircle, FaPaperPlane } from 'react-icons/fa';
import CreateSituation from './CreateSituation';
import SituationsAndGroups from './SituationsAndGroups';
import CreateKey from './CreateKey';
import { useNavigate } from 'react-router-dom';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import TableOfContents from './TableOfContents';
import { Tooltip } from 'react-tooltip';
import { checkValidity } from '../../../utils/utils';
import { useScenario } from '../../../context/ScenarioContext';
import { TUTORIAL_TARGETS, tutorialAttr, useTutorial } from '../../../tutorials';

/**
 * The palette the editor opens with on a large screen. Dragging situations onto
 * the canvas is what an author comes here to do, and the panel floats over the
 * left of the canvas rather than displacing it, so having it out costs nothing.
 * Phones get no default panel: there the panel covers the whole screen.
 */
const DEFAULT_PANEL = 'createSituation';

const CreateSidebar = ({ isMobile, renderEditSidebar, setRenderEditSidebar }) => {
    const {
        scenario,
        nodes,
        edges,
        graphErrors,
        setGraphErrors,
        copySituation,
        tableOfContents,
        setTableOfContents,
        publish,
        publishing,
        flushOps,
    } = useCanvas();

    const { showTocEditor, setShowTocEditor, permissionLevel, selectedNodeId } = useEditorUI();

    const { setCurrentSituationId } = useScenario();
    const [activePanel, setActivePanel] = useState(isMobile ? null : DEFAULT_PANEL);
    // Normally set by the panel's transitionend. A panel that starts open never
    // transitions, so it has to say so itself or the panels gated on this flag
    // would never render once the author switched to one of them.
    const [isFullyExpanded, setIsFullyExpanded] = useState(!isMobile);
    const navigate = useNavigate();

    // A step can say which panel it needs open. Declaring it rather than opening
    // it imperatively matters because this component remounts every time the
    // user comes back from a preview, which would otherwise close the palette
    // halfway through a sequence of drags.
    const { isActive: tutorialActive, step: tutorialStep } = useTutorial();
    const requestedPanel = tutorialActive ? tutorialStep?.sidebarPanel : null;

    useEffect(() => {
        if (requestedPanel) setActivePanel(requestedPanel);
    }, [requestedPanel]);

    const isButtonDisabled = !selectedNodeId;

    // Whether a table of contents exists is a property of the draft, not a flag to
    // keep in step with it.
    const hasToc = !!tableOfContents;

    const togglePanel = (panelName) => {
        setActivePanel(prev => (prev === panelName ? null : panelName));
        if (isMobile && setRenderEditSidebar) {
            setRenderEditSidebar(false); 
        }
    };

    const toggleCreateSituation = () => {
        togglePanel('createSituation');
    };

    const toggleSituationsList = () => {
        togglePanel('situationsList');
    };


    const toggleCreateKey = () => {
        togglePanel('createKey');
    };

    const handleSettingsClick = () => {
        navigate(`/create/${scenario?._id}`);
    };

    // Creating one is just the first draft edit: an empty table of contents op.
    // It used to be a dedicated endpoint that wrote the live scenario.
    const createTableOfContents = () => {
        if (!hasToc) {
            setTableOfContents({ contents: [{ sections: [], items: [] }], disabled: false });
        }
        setShowTocEditor(true);
    };
    

    // The preview loads the draft from the server, so anything still queued has
    // to land first. Whatever was selected is handed along to come back to.
    const handleClickPreview = async () => {
        await flushOps();
        setCurrentSituationId(scenario._id, null);
        navigate(`/create/${scenario._id}/preview`, {
            state: { returnTo: { nodeId: selectedNodeId, expanded: false } },
        });
    }

    const handleExitClick = () => {
        navigate('/home');
    };

    /**
     * Publishing validates first — locally for instant feedback, then on the
     * server, which is authoritative. Either way the failures come back as
     * `graphErrors` and ErrorOverlay renders them, so a failed publish leaves the
     * author looking at exactly what is wrong instead of navigating away.
     */
    const handlePublishClick = async () => {
        const result = await publish();
        if (result.published) navigate('/home');
        else if (result.message) console.error(result.message);
    };

    
    const handleTransitionEnd = () => {
        if (activePanel) {
            setIsFullyExpanded(true); 
        } else {
            setIsFullyExpanded(false); 
        }
    };

    
    // Opening the edit sidebar collapses whichever panel is out — but only when
    // it actually opens. It is already open on mount, and reacting to that would
    // close the palette the editor deliberately starts with. A tutorial step
    // that asks for a panel outranks this either way, so the palette does not
    // vanish from under someone mid-drag.
    const editSidebarWasOpen = useRef(renderEditSidebar);
    useEffect(() => {
        const justOpened = renderEditSidebar && !editSidebarWasOpen.current;
        editSidebarWasOpen.current = renderEditSidebar;
        if (justOpened && !requestedPanel) {
            setActivePanel(null);
        }
    }, [renderEditSidebar, requestedPanel]);

    const collapsePanel = () => {
        setActivePanel(null);
    };

    return (
        <>
            <div className={styles.container}>
                {/* Narrow Sidebar with Icons */}
                <div className={styles.iconSidebar}>
                    <div
                        className={`${styles.iconItem} ${activePanel === 'createSituation' ? styles.activeIcon : ''}`}
                        onClick={toggleCreateSituation}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Create Situation"
                        {...tutorialAttr(TUTORIAL_TARGETS.RAIL_CREATE_SITUATION)}
                    >
                        <FaShapes />
                    </div>
                    <div
                        className={`${styles.iconItem} ${activePanel === 'situationsList' ? styles.activeIcon : ''}`}
                        onClick={toggleSituationsList}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Situations and Groups"
                    >
                        <FaIndent />
                    </div>
                    <div
                        className={`${styles.iconItem} ${activePanel === 'createKey' ? styles.activeIcon : ''}`}
                        onClick={toggleCreateKey}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Keys (unlock or hide choices)"
                        {...tutorialAttr(TUTORIAL_TARGETS.RAIL_KEYS)}
                    >
                        <FaKey />
                    </div>
                    {permissionLevel === 'creator' && 
                    <div
                        className={styles.iconItem}
                        onClick={handleSettingsClick}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Scenario Settings"
                    >
                        <FaCog />
                    </div>
                    }
                    {permissionLevel === 'creator' && 
                    <div
                        className={styles.iconItem}
                        onClick={createTableOfContents}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Table of Contents"
                    >
                        <FaList />
                    </div>
                    }
                    <div
                        className={`${styles.iconItem} ${isButtonDisabled ? styles.disabledIcon : ''}`}
                        onClick={() => {
                            if (!isButtonDisabled) {
                                copySituation(selectedNodeId);
                                if (!isMobile) {
                                    setRenderEditSidebar(true);
                                }
                            } 
                        }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Copy Node"
                        aria-disabled={isButtonDisabled}
                    >
                        <FaCopy />
                    </div>

                    <div
                        className={styles.iconItem}
                        onClick={handleClickPreview}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Preview Scenario"
                        {...tutorialAttr(TUTORIAL_TARGETS.RAIL_PREVIEW)}
                    >
                        <FaEye />
                    </div>
                    <div
                        className={styles.iconItem}
                        onClick={() => {
                            const errors = checkValidity(nodes, edges);
                            setGraphErrors(errors);
                        }}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Check Validity"
                        {...tutorialAttr(TUTORIAL_TARGETS.RAIL_CHECK_VALIDITY)}
                    >
                        {graphErrors === null ? (
                            <FaSearch />
                        ) : graphErrors.length > 0 ? (
                            <FaExclamationCircle style={{ color: 'red' }} />
                        ) : (
                            <FaCheckCircle style={{ color: 'green' }} />
                        )}
                    </div>
                    {permissionLevel === 'creator' &&
                        <div
                            className={`${styles.iconItem} ${publishing ? styles.disabledIcon : ''}`}
                            onClick={handlePublishClick}
                            data-tooltip-id="tooltip"
                            data-tooltip-content="Publish"
                            aria-disabled={publishing}
                            {...tutorialAttr(TUTORIAL_TARGETS.RAIL_PUBLISH)}
                        >
                            <FaPaperPlane />
                        </div>
                    }
                    
                    <div
                        className={styles.iconItem}
                        onClick={handleExitClick}
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Exit"
                    >
                        <FaSignOutAlt />
                    </div>
                </div>
                {/* Expanded Panel */}
                <div
                    className={`${styles.expandedPanel} ${activePanel ? styles.activePanel : ''}`}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {activePanel === 'createSituation' && <CreateSituation isMobile={isMobile} collapsePanel={collapsePanel} />}
                    {activePanel === 'situationsList' && isFullyExpanded && <SituationsAndGroups setRenderEditSidebar={setRenderEditSidebar} />}
                    {activePanel === 'createKey' && isFullyExpanded && <CreateKey setRenderEditSidebar={setRenderEditSidebar} />}
                </div>
            </div>
            {showTocEditor && (
                <div className={styles.tocContainer}>
                    <TableOfContents />
                </div>
            )}
            <Tooltip
                id="tooltip"
                place="right"
                type="dark"
                effect="solid"
                style={{ zIndex: 9999 }}
            />
        </>
    );
};

export default CreateSidebar;
